import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import LeadsContext from '../../providers/Leads/Leads.context';
import classNames from 'classnames';
import { SORTING_VALUES, CUSTOM_COL_SIZES, LeadsTableColumnsKey } from './data';
import { BODY_CONTENT_GENERATE } from './components/LeadsTableCell';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import api from 'api';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { Lead } from 'models/Lead';
import ShowInactiveLeads from '../ShowInactiveLeads';
import {
  INACTIVE_PAGE,
  ONGOING_PAGE,
} from '../../providers/Leads/Leads.provider';
import ResolvePagination from 'components/ResolvePagination';
import { useTranslation } from 'react-i18next';
import DealContext from '../../providers/Deal/Deal.context';
import EditColumnsVisibilityButton from 'components/EditColumnsVisibilityButton';
import { SIZE } from 'models/Size';
import Counter, {
  COUNTER_VARIANTS,
} from 'components/Counter/Counter.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import ReorderFilesDrawer from './components/ReorderFilesDrawer';
import useLead from '../../hooks/useLead';

import './LeadsTableContainer.styles.scss';

type LeadsTableContainerProps = {
  className?: string;
};

const LeadsTableContainer: React.FC<LeadsTableContainerProps> = (props) => {
  const { className } = props;

  const [reorderFilesDrawer, reorderFilesDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  const {
    showInactiveLeads,
    totalOngoingPages,
    onGoingTableLeads,
    waitlistedTableLeads,
    totalWaitlistedPages,
    isLoadingOngoingTable,
    isLoadingWaitlistedTable,
    totalUnreadChatsCount,
    totalUnrepliedChatsCount,
    numberOfWaitlistedGridLeads,
    updateLead,
    removeLead,
    openLeadDrawer,
  } = useContext(LeadsContext);
  const { dealRoom, dealRoomTopNodes, setDealRoom, openManageListView } =
    useContext(DealContext);
  const {
    currentUser: { _id: currentUserId, leadTableColumns },
    setCurrentUser,
  } = useContext(CurrentUserContext);

  const classes = classNames('leads-table-container', className);

  const { t } = useTranslation();

  const { getBackgroundColor } = useLead();

  const onReorderL = useCallback(
    async (leadTableColumns: string[]) => {
      setCurrentUser((prev) => ({ ...prev, leadTableColumns }));

      api.users.patchUser(currentUserId, {
        leadTableColumns,
      });
    },
    [currentUserId, setCurrentUser],
  );

  const tableHeaderTransformations: Record<string, ReactNode> = useMemo(
    () => ({
      chat: () => (
        <div className="lds-chat-header">
          <Counter
            count={totalUnreadChatsCount || totalUnrepliedChatsCount}
            isPositionAbsolute
            size={SIZE.SMALL}
            variant={
              totalUnreadChatsCount
                ? COUNTER_VARIANTS.ORANGE
                : COUNTER_VARIANTS.SILVER
            }
          >
            {totalUnreadChatsCount || totalUnrepliedChatsCount}
          </Counter>

          {t('chat')}
        </div>
      ),
    }),
    [totalUnreadChatsCount, totalUnrepliedChatsCount, t],
  );

  const tableContentTransformations = useMemo(
    () =>
      (leadTableColumns as LeadsTableColumnsKey[]).reduce(
        (acc, col) => ({
          ...acc,
          [col]: (lead: Lead) =>
            BODY_CONTENT_GENERATE[col]({
              lead,
              updateLead,
              removeLead,
            }),
        }),
        {},
      ),
    [leadTableColumns, removeLead, updateLead],
  );

  const showFileAccessSettings = useMemo(() => {
    return !!dealRoomTopNodes.length && onGoingTableLeads[0]?.canManageDataRoom;
  }, [dealRoomTopNodes.length, onGoingTableLeads]);

  const openReorderFilesDrawer = useCallback(() => {
    reorderFilesDrawer?.setOpen(true);
  }, [reorderFilesDrawer]);

  return (
    <div className={classes}>
      <ReorderResizeTable
        columns={leadTableColumns as Array<keyof Lead>}
        sortingValues={SORTING_VALUES}
        isDataLoading={isLoadingOngoingTable}
        data={onGoingTableLeads}
        hasHorizontalScrollFade
        settingsValues={
          showFileAccessSettings
            ? ({
                fileAccess: openReorderFilesDrawer,
              } as any)
            : []
        }
        customSizes={CUSTOM_COL_SIZES}
        showPlacehodler={false}
        tableHeaderContentTransformations={tableHeaderTransformations}
        tableContentTransformations={tableContentTransformations}
        getRowBackgroundColor={getBackgroundColor}
        onReorder={onReorderL}
        onRowClick={openLeadDrawer}
      />

      <ReorderFilesDrawer
        ref={reorderFilesDrawerRef}
        dealRoom={dealRoom}
        dealRoomTopNodes={dealRoomTopNodes}
        onClose={(dataRoomTopNodes) =>
          setDealRoom((prev) => ({ ...prev, dataRoomTopNodes }))
        }
      />

      <EditColumnsVisibilityButton onClick={openManageListView} />

      <ResolvePagination
        pageParam={ONGOING_PAGE}
        totalPages={totalOngoingPages}
      />

      <ShowInactiveLeads />

      {showInactiveLeads && !!numberOfWaitlistedGridLeads && (
        <>
          <ReorderResizeTable
            tableContentTransformations={tableContentTransformations}
            columns={leadTableColumns as Array<keyof Lead>}
            sortingValues={SORTING_VALUES}
            isDataLoading={isLoadingWaitlistedTable}
            data={waitlistedTableLeads}
            customSizes={CUSTOM_COL_SIZES}
            showPlacehodler={false}
            hasHorizontalScrollFade
            getRowBackgroundColor={getBackgroundColor}
            onReorder={onReorderL}
            onRowClick={openLeadDrawer}
          />

          <ResolvePagination
            pageParam={INACTIVE_PAGE}
            totalPages={totalWaitlistedPages}
          />
        </>
      )}
    </div>
  );
};

export default LeadsTableContainer;
